/**
 * Conditional Required Logic evaluator.
 *
 * The static `required` flag is the initial source of truth (exactly like `hidden`
 * is for conditional logic). A `requiredLogic` / `cellRequiredLogic` rule then modifies
 * that value, but ONLY when its conditions pass — otherwise it falls back to the initial
 * value. This mirrors how `applyLogic` / `customApplyLogic` treat `hidden`.
 *
 * Resolution layering (least- to most-specific, most specific wins):
 *   - cell:  column.required -> column.requiredLogic -> column.cellRequiredLogic
 *   - field: field.required  -> field.requiredLogic
 *
 * This module is intentionally standalone and does not modify any Conditional Logic code.
 */

import ConditionalLogic from '../constants/ConditionalLogic';

const { conditions: OPERATORS, requiredActions } = ConditionalLogic;

/**
 * Evaluate a single condition against a resolved cell/field value.
 *
 * @param {String} condition - operator (=, !=, *=, null=, ?=, >, <)
 * @param {*} cellValue - the value being tested
 * @param {*} value - the target value from the condition
 * @returns {Boolean}
 */
const evaluateCondition = (condition, cellValue, value) => {
  switch (condition) {
    case OPERATORS.equal: // '='
      return (
        (!Array.isArray(cellValue) && new RegExp(`^${value}$`, 'i').test(cellValue)) ||
        (Array.isArray(cellValue) && cellValue.includes(value))
      );

    case OPERATORS.notEqual: // '!='
      return (
        (!Array.isArray(cellValue) && !(new RegExp(`^${value}$`, 'i').test(cellValue))) ||
        (Array.isArray(cellValue) && !cellValue.includes(value))
      );

    case OPERATORS.filled: // '*='
      return (
        (cellValue && !Array.isArray(cellValue)) ||
        (Array.isArray(cellValue) && cellValue.length > 0)
      );

    case OPERATORS.empty: // 'null='
      return (
        cellValue === '' ||
        cellValue === undefined ||
        cellValue === null ||
        (Array.isArray(cellValue) && cellValue.length < 1)
      );

    case OPERATORS.contain: // '?='
      return typeof cellValue === 'string' && new RegExp(value, 'gi').test(cellValue);

    case OPERATORS.greaterThan: // '>'
      return cellValue > value;

    case OPERATORS.lessThan: // '<'
      return cellValue < value;

    default:
      return false;
  }
};

/**
 * Resolve the value a condition references. Conditions can point at either a document
 * field (via `field`, using docFields) or a sibling cell in the same row (via `column`).
 *
 * @param {Object} conditionEntry
 * @param {Object} rowCells
 * @param {Array} docFields
 * @returns {*}
 */
const getConditionValue = (conditionEntry, rowCells, docFields) => {
  const { column, field } = conditionEntry;

  if (field) {
    const targetField = (docFields || []).find((f) => f._id === field);
    return targetField ? targetField.value : undefined;
  }

  return rowCells ? rowCells[column] : undefined;
};

/**
 * Evaluate a conditions[] array to a single boolean using the eval type.
 *
 * @param {Array} conditions
 * @param {String} evalType - 'and' | 'or'
 * @param {Object} rowCells - sibling cell values (used for `column` conditions)
 * @param {Array} docFields - all document fields (used for `field` conditions)
 * @returns {Boolean}
 */
export const evaluateConditions = (conditions, evalType, rowCells = {}, docFields = []) => {
  if (!Array.isArray(conditions) || conditions.length === 0) return false;

  const evaluations = conditions.map((conditionEntry) => {
    const { condition, value } = conditionEntry;
    const cellValue = getConditionValue(conditionEntry, rowCells, docFields);
    return evaluateCondition(condition, cellValue, value);
  });

  return evalType === ConditionalLogic.evals.or
    ? evaluations.some(Boolean)
    : evaluations.every(Boolean);
};

/**
 * Apply a single requiredLogic rule on top of an initial `fallback` required value.
 *
 * A present + valid rule FULLY GOVERNS the effective required state (it overrides the
 * static/lower-layer value entirely, in both directions). The `fallback` is only used
 * when there is no valid rule. This mirrors how conditional logic resolves `hidden` at
 * read-time: the static value is just the initial state, never mutated.
 *
 *   - enforce   -> `passed`  (required when conditions match, not required otherwise)
 *   - unenforce -> `!passed` (not required when conditions match, required otherwise)
 *
 * @param {Object} requiredLogic - { action: 'enforce' | 'unenforce', eval, conditions }
 * @param {Boolean} fallback - the required value to use when no valid rule applies
 * @param {Object} rowCells
 * @param {Array} docFields
 * @returns {Boolean}
 */
export const applyRequiredLogic = (requiredLogic, fallback, rowCells = {}, docFields = []) => {
  const hasLogic = !!(
    requiredLogic &&
    requiredLogic.action &&
    requiredLogic.eval &&
    Array.isArray(requiredLogic.conditions) &&
    requiredLogic.conditions.length > 0
  );

  if (!hasLogic) return fallback;

  const passed = evaluateConditions(
    requiredLogic.conditions,
    requiredLogic.eval,
    rowCells,
    docFields
  );

  if (requiredLogic.action === requiredActions.enforce) {
    return passed;
  }

  // 'unenforce'
  return !passed;
};

/**
 * Resolve the effective required state for a column's cell.
 * Layering: static column.required -> column.requiredLogic -> column.cellRequiredLogic.
 *
 * @param {Object} column
 * @param {Object} rowCells - the row's cells (for sibling `column` conditions)
 * @param {Array} docFields
 * @returns {Boolean}
 */
export const resolveColumnRequired = (column, rowCells = {}, docFields = []) => {
  if (!column) return false;

  let required = !!column.required;
  required = applyRequiredLogic(column.requiredLogic, required, rowCells, docFields);
  required = applyRequiredLogic(column.cellRequiredLogic, required, rowCells, docFields);

  return required;
};

/**
 * Resolve the effective required state for a field.
 * Layering: static field.required -> field.requiredLogic.
 *
 * @param {Object} field
 * @param {Array} docFields
 * @returns {Boolean}
 */
export const resolveFieldRequired = (field, docFields = []) => {
  if (!field) return false;
  return applyRequiredLogic(field.requiredLogic, !!field.required, {}, docFields);
};

/**
 * Resolve the effective required state for a nested collection schema table.
 * Layering: static schemaEntry.required -> schemaEntry.requiredLogic.
 * Column conditions resolve against the parent row's cells; field conditions against docFields.
 *
 * @param {Object} schemaEntry
 * @param {Object} parentCells - the parent row's cells (for sibling `column` conditions)
 * @param {Array} docFields
 * @returns {Boolean}
 */
export const resolveSchemaTableRequired = (schemaEntry, parentCells = {}, docFields = []) => {
  if (!schemaEntry) return false;
  return applyRequiredLogic(schemaEntry.requiredLogic, !!schemaEntry.required, parentCells, docFields);
};

export default {
  evaluateConditions,
  applyRequiredLogic,
  resolveColumnRequired,
  resolveFieldRequired,
  resolveSchemaTableRequired,
};
